/**
 * Which planner layout a viewport should get (#646).
 *
 * The app used to pick this from the device's user agent. `isDesktopWeb()`
 * classifies iPadOS as mobile — it reports `MacIntel` with `maxTouchPoints > 1`
 * — so every desktop branch in `TheWeeklyScreen` was unreachable on a tablet at
 * any width. A 1366px iPad in landscape ran the phone column and left 47.3% of
 * the screen unused, while the seven-day grid it should have been running sat
 * right there, built for exactly that shape.
 *
 * So decide on the viewport's SHAPE instead. Portrait keeps the #632 content
 * column; landscape gets the grid.
 */
import { Platform, useWindowDimensions } from 'react-native';
import { isDesktopWeb } from '../utils/platformDetection';

/**
 * 'column'  — the #632 content column (`WeeklyMobile`)
 * 'grid'    — the v2 seven-day grid (`WeeklyDesktop`)
 * 'desktop' — not our call: defer to the existing width branches in
 *             `TheWeeklyScreen`, so this resolver can never change desktop.
 */
export type PlannerLayout = 'column' | 'grid' | 'desktop';

/**
 * Below this, a landscape viewport keeps the column.
 *
 * Sits under every iPad landscape width (9.7"/10.2" 1024, 10.5" 1112, 11" 1180,
 * 12.9" 1366) and above every phone landscape width (iPhone 15 Pro Max is 932),
 * so a phone held sideways never gets a seven-column grid. Inclusive, because
 * the most common iPad is exactly 1024.
 */
export const TABLET_GRID_MIN_WIDTH = 1024;

export function resolvePlannerLayout(input: {
  width: number;
  height: number;
  /** `isDesktopWeb()` */
  isDesktop: boolean;
  /** `Platform.OS === 'web'` */
  isWeb: boolean;
}): PlannerLayout {
  const { width, height, isDesktop, isWeb } = input;

  // The grid is CSS Grid (SlotGrid uses `display: 'grid'`), web-only by
  // construction. Native tablets stay on the column.
  if (!isWeb) return 'column';

  // Desktop keeps whatever it does today. Deliberately not re-deriving it here.
  if (isDesktop) return 'desktop';

  // Touch web. Shape decides. A square viewport counts as portrait.
  const isLandscape = width > height;
  return isLandscape && width >= TABLET_GRID_MIN_WIDTH ? 'grid' : 'column';
}

/** `resolvePlannerLayout` bound to the live viewport. Re-runs on rotation. */
export function usePlannerLayout(): PlannerLayout {
  const { width, height } = useWindowDimensions();
  return resolvePlannerLayout({
    width,
    height,
    isDesktop: isDesktopWeb(),
    isWeb: Platform.OS === 'web',
  });
}
